import {
  AnalyticsEventName,
  AnalyticsPageType,
  getClientBrowserParameters,
  sendShopifyAnalytics,
} from '@shopify/hydrogen-react'

/**
 * Tells Shopify Analytics that someone is on this funnel (x1.gritvuegear.com), the same
 * way Shopify's own headless storefronts do. Visits show up in Shopify → Analytics.
 *
 * Only the PUBLIC Storefront token goes here. Never put a private Admin token (shpat_...)
 * in this file: everything in the page's code can be read by anyone.
 */
export const SHOPIFY_STORE_DOMAIN = 'shop.gritvuegear.com'
export const SHOPIFY_STOREFRONT_PUBLIC_TOKEN = '9cfdcfed1abd24c3c7ec482a422c85c9'
/** Optional: the Headless storefront ID (Shopify → Headless → your storefront). Leave '' if unknown. */
export const SHOPIFY_STOREFRONT_ID = ''
/** Parent domain so Shopify's visitor cookies are shared between the funnel and the checkout. */
export const SHOPIFY_COOKIE_DOMAIN = 'gritvuegear.com'

const API_VERSION = '2024-10'
const SHOP_ID_KEY = 'gv_shopify_shop_id'

let shopIdPromise: Promise<string | null> | null = null

/** Reads the store's ID from Shopify with the public token, once per visit. */
export function getShopId(): Promise<string | null> {
  if (shopIdPromise) return shopIdPromise
  shopIdPromise = (async () => {
    try {
      const cached = sessionStorage.getItem(SHOP_ID_KEY)
      if (cached) return cached
    } catch {
      // storage blocked: fetch below
    }
    try {
      const response = await fetch(`https://${SHOPIFY_STORE_DOMAIN}/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_PUBLIC_TOKEN,
        },
        body: JSON.stringify({ query: '{ shop { id } }' }),
      })
      if (!response.ok) throw new Error(`Storefront API ${response.status}`)
      const json = (await response.json()) as { data?: { shop?: { id?: string } } }
      const id = json.data?.shop?.id ?? null
      if (id) {
        try {
          sessionStorage.setItem(SHOP_ID_KEY, id)
        } catch {
          // ignore
        }
      }
      return id
    } catch (error) {
      console.warn('[GritVue] Shopify shop ID could not be loaded; Shopify visit tracking is off.', error)
      shopIdPromise = null
      return null
    }
  })()
  return shopIdPromise
}

/** Sends one page view to Shopify Analytics. Fails silently: it never affects the page. */
export async function sendShopifyPageView(pathname: string) {
  if (typeof window === 'undefined') return
  const shopId = await getShopId()
  if (!shopId) return
  try {
    await sendShopifyAnalytics(
      {
        eventName: AnalyticsEventName.PAGE_VIEW,
        payload: {
          ...getClientBrowserParameters(),
          hasUserConsent: true,
          shopId,
          currency: 'USD',
          acceptedLanguage: 'EN',
          pageType: pathname === '/' ? AnalyticsPageType.product : AnalyticsPageType.page,
          ...(SHOPIFY_STOREFRONT_ID ? { storefrontId: SHOPIFY_STOREFRONT_ID } : {}),
        },
      },
      SHOPIFY_STORE_DOMAIN,
    )
  } catch (error) {
    console.warn('[GritVue] Shopify page view not sent.', error)
  }
}
